/**
 * Constants for the Legal Contract Review application
 */

/**
 * Default jurisdictions
 */
export const DEFAULT_JURISDICTIONS = [
  'US (Alabama)',
  'US (Alaska)',
  'US (Arizona)',
  'US (Arkansas)',
  'US (California)',
  'US (Colorado)',
  'US (Connecticut)',
  'US (Delaware)',
  'US (Florida)',
  'US (Georgia)',
  'US (Hawaii)',
  'US (Idaho)',
  'US (Illinois)',
  'US (Indiana)',
  'US (Iowa)',
  'US (Kansas)',
  'US (Kentucky)',
  'US (Louisiana)',
  'US (Maine)',
  'US (Maryland)',
  'US (Massachusetts)',
  'US (Michigan)',
  'US (Minnesota)',
  'US (Mississippi)',
  'US (Missouri)',
  'US (Montana)',
  'US (Nebraska)',
  'US (Nevada)',
  'US (New Hampshire)',
  'US (New Jersey)',
  'US (New York)',
  'US (New Mexico)',
  'US (North Carolina)',
  'US (North Dakota)',
  'US (Ohio)',
  'US (Oklahoma)',
  'US (Oregon)',
  'US (Pennsylvania)',
  'US (Rhode Island)',
  'US (South Carolina)',
  'US (South Dakota)',
  'US (Tennessee)',
  'US (Texas)',
  'US (Utah)',
  'US (Vermont)',
  'US (Virginia)',
  'US (Washington)',
  'US (West Virginia)',
  'US (Wisconsin)',
  'US (Wyoming)',
  'Austria',
  'Belgium',
  'Bulgaria',
  'Croatia',
  'Cyprus',
  'Czechia',
  'Denmark',
  'Estonia',
  'Finland',
  'France',
  'Germany',
  'Greece',
  'Hungary',
  'Iceland',
  'Ireland',
  'Italy',
  'Latvia',
  'Liechtenstein',
  'Lithuania',
  'Luxembourg',
  'Malta',
  'Netherlands',
  'Norway',
  'Poland',
  'Portugal',
  'Romania',
  'Slovakia',
  'Slovenia',
  'Spain',
  'Sweden',
  'United Kingdom',
  'Other',
] as const

/**
 * Default contract types
 */
export const DEFAULT_CONTRACT_TYPES = [
  { value: 'MSA', label: 'Master Services Agreement' },
  { value: 'NDA', label: 'Non-Disclosure Agreement' },
  { value: 'SOW', label: 'Statement of Work' },
  { value: 'Employment Agreement', label: 'Employment Agreement' },
  { value: 'License Agreement', label: 'License Agreement' },
  { value: 'Service Agreement', label: 'Service Agreement' },
  { value: 'Other', label: 'Other' },
] as const

/**
 * Default roles
 */
export const DEFAULT_ROLES: Record<string, string> = {
  'senior-counsel': 'Senior Legal Counsel',
  'managing-partner': 'Managing Partner',
  'associate': 'Associate Attorney',
  'client': 'Client',
}

/**
 * Default role instructions
 */
export const DEFAULT_ROLE_INSTRUCTIONS: Record<string, string> = {
  'senior-counsel': `You are reviewing this contract for a Senior Legal Counsel. Provide:
- High-level strategic analysis with focus on material risks
- Concise summaries assuming legal expertise
- Key red flags and deal-breaker issues
- Strategic negotiation points
- References to relevant case law and precedents`,
  'managing-partner': `You are reviewing this contract for a Managing Partner. Provide:
- Business-focused analysis with commercial implications
- Risk/rebalance considerations for the business
- Impact on firm/client relationships
- Resource allocation implications (time, costs)
- Clear recommendations with business justification`,
  'associate': `You are reviewing this contract for an Associate Attorney. Provide:
- Detailed analysis with explanations of legal concepts
- Educational annotations for learning purposes
- Step-by-step reasoning for each assessment
- References to relevant legal principles
- Thorough breakdown of all clauses and implications`,
  'client': `You are reviewing this contract for a Client (non-lawyer). Provide:
- Plain language explanations avoiding legal jargon
- Practical implications in simple terms
- Focus on what affects them directly
- Clear "what this means for you" summaries
- Actionable recommendations explained simply`,
}

/**
 * Local storage keys
 */
export const STORAGE_KEYS = {
  JURISDICTIONS: 'savedJurisdictions',
  CONTRACT_TYPES: 'customContractTypes',
  ROLES: 'customRoles',
  ROLE_INSTRUCTIONS: 'customRoleInstructions',
} as const

/**
 * File upload constraints
 */
export const FILE_CONSTRAINTS = {
  MAX_SIZE: 10 * 1024 * 1024, // 10MB
  ALLOWED_TYPES: ['application/pdf', 'text/plain'],
  ALLOWED_EXTENSIONS: ['.pdf', '.txt'],
} as const

/**
 * SSE configuration
 */
export const SSE_CONFIG = {
  HEARTBEAT_TIMEOUT: 10000, // 10 seconds
} as const
